import type { RichTextProp, TestGroup } from '../../types';

export function countMarkdownListItems(content?: string | null): number {
  if (!content) return 0;
  return (content.match(/^-\s/gm) || []).length;
}

interface TestsMetaInput {
  hasGroups: boolean;
  isFlat: boolean;
  tests?: string[];
  testGroups?: TestGroup[];
  subDescription?: RichTextProp | null;
  description?: RichTextProp | null;
}

export interface TestsMeta {
  showToggle: boolean;
  testsCount: number;
}

export function getTestsMeta({
  hasGroups,
  isFlat,
  tests,
  testGroups,
  subDescription,
  description,
}: TestsMetaInput): TestsMeta {
  const count = isFlat
    ? (tests?.length ?? 0)
    : countMarkdownListItems(subDescription?.content);

  const showToggle = hasGroups
    ? (testGroups?.length ?? 0) > 2
    : isFlat
      ? count > 2
      : description
        ? count >= 2
        : count > 2;

  const testsCount = hasGroups
    ? (testGroups ?? []).reduce(
        (sum, group) => sum + (group.items?.length ?? 0),
        0,
      )
    : isFlat
      ? count
      : description
        ? count + 1
        : count;

  return { showToggle, testsCount };
}

export interface PriceDisplayOptions {
  /** Currency prefix for numeric values. Defaults to `£`. */
  currency?: string;
  /** Treat the value as preformatted display text. */
  isFormatted?: boolean;
  /** Optional label rendered before the value, e.g. `From`. */
  label?: string | null;
}

export interface DisplayPrice {
  /** False when the package has no price to show. */
  hasPrice: boolean;
  /** True when the value was numeric and received currency formatting. */
  isNumber: boolean;
  displayPrice: string | null;
  /** Display value including the optional label, ready for rendering. */
  priceText: string | null;
}

export function getDisplayPrice(
  price?: string | null,
  { currency = '£', isFormatted = false, label }: PriceDisplayOptions = {},
): DisplayPrice {
  const raw = typeof price === 'string' ? price.trim() : '';

  if (!raw) {
    return {
      hasPrice: false,
      isNumber: false,
      displayPrice: null,
      priceText: null,
    };
  }

  const numeric = Number(raw);
  const isNumber = !isFormatted && !Number.isNaN(numeric);
  const displayPrice = isNumber ? numeric.toLocaleString() : raw;
  const value = isNumber ? `${currency}${displayPrice}` : displayPrice;
  const prefix = label?.trim();

  return {
    hasPrice: true,
    isNumber,
    displayPrice,
    priceText: prefix ? `${prefix} ${value}` : value,
  };
}
